import React from 'react';
import { connect, ResolveThunks } from 'react-redux';
import { Box } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';

import { backdropImageUrl } from '@paragon/client-tools';
import { isUsingCustomTheme, LoginContainer } from '@paragon/login-tools';
import { NotifierDialog } from '@paragon/notification-tools';
import { getSpinnersToDisplay, SpinnerMessage } from '@paragon/task-spinner';
import { ThemedLogo } from '@paragon/themes';
import { WalkthroughBlurbAnchor } from '@paragon/tour-guide';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import designLogoUrl from '../../resources/design.png';
import { RootState } from '../store';
import {
  BEARING_IMAGE_UPLOAD_DIALOG,
  CARRIED_BEARING_DELETION_CONFIRMATION_DIALOG,
  CHOOSE_PROJECT_DIALOG,
  COMPONENT_DELETION_CONFIRMATION_DIALOG,
  CONFIRM_AFFECT_ALL_LOCKS_DIALOG,
  CONFIRM_CANCEL_INSERTION_DIALOG,
  CONFIRM_REPLACE_LAYOUT_DIALOG,
  CREATE_ESTIMATE_DIALOG,
  DELETE_ROOF_CONTAINER_CONFIRMATION_DIALOG,
  DOWNLOADS_DIALOG,
  EDIT_DEPTH_DIALOG,
  FLIP_TRUSS_ENVELOPE_DIALOG,
  HOTKEY_DIALOG,
  IMPORT_ASSEMBLY_GROUP_DIALOG,
  INSERTION_MENU_DIALOG,
  LOG_IN_DIALOG,
  NOT_ON_MOBILE_DIALOG,
  PARENT_TRUSS_GROUP_DELETION_CONFIRMATION_DIALOG,
  PARTS_DIALOG,
  PRESETS_DIALOG,
  PROJECT_WIDE_SETTINGS_DIALOG,
  QUALITY_CONTROL_DIGITAL_QC_DOWNLOAD_DIALOG,
  QUALITY_CONTROL_PDF_DOWNLOAD_DIALOG,
  SELECT_COMPONENT_DESIGN_DIALOG,
  SELECT_PLATE_LISTS_DIALOG,
  SELECT_SCHEDULE_LINE_GROUP_DIALOG,
  SELECT_STATION_DIALOG,
  SHARE_DIALOG,
  SHARED_COMPONENT_SET_INFO_DIALOG,
  SIGN_UP_DIALOG,
  UPGRADE_ACCOUNT_DIALOG,
  USER_SETTINGS_DIALOG,
  WELCOME_DIALOG,
} from '../store/actions/popupActions';
import { getIsInSharedComponentSetContext, startApp } from '../store/actions/startAppActions';
import { isLayoutModeEnabled } from '../store/selectors/layoutModeSelectors';
import { shouldShowAnalysisDialog, shouldShowUploadDialog } from '../store/selectors/longRunningOperationSelectors';
import { getRightSidebarInfo, getShouldRightSidebarBeInLegacyMode } from '../store/selectors/rightToolbarSelectors';
import SceneCanvas from '../three/SceneCanvas';
import { AssistantRoot } from './assistant/AssistantRoot';
import { BearingImageUploadDialog } from './dialogs/BearingImageUploadDialog';
import { CarriedBearingDeleteConfirmDialog } from './dialogs/CarriedBearingDeleteConfirmDialog';
import { ChooseProjectDialog } from './dialogs/choose-project/ChooseProjectDialog';
import { ConfirmAffectAllLocksDialog } from './dialogs/ConfirmAffectAllLocksDialog';
import { ConfirmCancelInsertionDialog } from './dialogs/ConfirmCancelInsertionDialog';
import { ConnectedUserSettingsDialog } from './dialogs/ConnectedUserSettingsDialog';
import { CreateEstimateDialog } from './dialogs/CreateEstimateDialog';
import { DeleteComponentDesignConfirmDialog } from './dialogs/DeleteComponentDesignConfirmDialog';
import { DeleteRoofContainerConfirmDialog } from './dialogs/DeleteRoofContainerConfirmDialog';
import { DownloadPlateListComparisonDialog } from './dialogs/download/DownloadPlateListComparisonDialog';
import { DownloadsDialog } from './dialogs/download/DownloadsDialog';
import { EditDepthDialog } from './dialogs/EditDepthDialog';
import { FlipTrussEnvelopeConfirmDialog } from './dialogs/FlipTrussEnvelopeConfirmDialog';
import { Hotkeys } from './dialogs/Hotkeys';
import { ImportAssemblyGroupDialog } from './dialogs/ImportAssemblyGroupDialog';
import { InsertionMenuDialog } from './dialogs/InsertionMenuDialog';
import { LogInDialog } from './dialogs/LogInDialog';
import { AnalyzeDialog } from './dialogs/long-running-progress/analyze/AnalyzeDialog';
import { UploadDialog } from './dialogs/long-running-progress/upload/UploadDialog';
import { NotOnMobileDialog } from './dialogs/NotOnMobileDialog';
import { ParentTrussGroupDeleteConfirmDialog } from './dialogs/ParentTrussGroupDeleteConfirmDialog';
import { PartsDialog } from './dialogs/PartsDialog';
import { PresetsDialog } from './dialogs/presets/PresetsDialog';
import { ProjectWideSettingsDialog } from './dialogs/project-wide-settings/ProjectWideSettingsDialog';
import { QualityControlDigitalQcDownloadDialog } from './dialogs/QualityControlDigitalQcDownloadDialog';
import { QualityControlPdfDownloadDialog } from './dialogs/QualityControlPdfDownloadDialog';
import { ReplaceLayoutConfirmDialog } from './dialogs/ReplaceLayoutConfirmDialog';
import { SelectComponentDesignDialog } from './dialogs/SelectComponentDesignDialog';
import { SelectScheduleLineGroupDialog } from './dialogs/SelectScheduleLineGroupDialog';
import SelectStationDialog from './dialogs/SelectStationDialog';
import { ShareDialog } from './dialogs/share/ShareDialog';
import { SharedComponentSetInfoDialog } from './dialogs/SharedComponentSetInfoDialog';
import { SignUpDialog } from './dialogs/SignUpDialog';
import { UnderlayUploadDialog } from './dialogs/UnderlayUploadDialog';
import { UpgradeAccountDialog } from './dialogs/UpgradeAccountDialog';
import { WelcomeDialog } from './dialogs/WelcomeDialog';
import Header from './header/Header';
import { withInMobileMode } from './hooks';
import AddItemPanel from './interface/panels/AddItemPanel';
import { MatchFasciaHeightsPanel } from './interface/panels/MatchFasciaHeightsPanel';
import { OptimizationPanel } from './interface/panels/OptimizationPanel';
import { RenameLayoutComponentsPanel } from './interface/panels/RenameLayoutComponentsPanel';
import { TransformPanel } from './interface/panels/transform/TransformPanel';
import { HeelMacrosPanel } from './interface/panels/truss-edits/HeelMacrosPanel';
import { MakeGablePanel } from './interface/panels/truss-edits/MakeGablePanel';
import { OverhangMacrosPanel } from './interface/panels/truss-edits/OverhangMacrosPanel';
import { RewebProfileTrussPanel } from './interface/panels/truss-edits/RewebProfileTrussPanel';
import { RewebZonePanel } from './interface/panels/truss-edits/RewebZonePanel';
import { SpanMacrosPanel } from './interface/panels/truss-edits/SpanMacrosPanel';
import { StubTrussPanel } from './interface/panels/truss-edits/StubTrussPanel';
import { ImportComponentDesignPanel } from './interface/subpanels/import-component/ImportComponentDesignPanel';
import { ReplaceLumbersPanel } from './interface/subpanels/replace-materials/ReplaceLumberPanel';
import SettingsPanel from './interface/subpanels/settings/SettingsPanel';
import { BrowserTabTitle } from './misc/BrowserTabTitle';
import CoordinatesLabel from './misc/CoordinatesLabel';
import { DesignClientNotifier } from './misc/DesignClientNotifier';
import { DocsPanel, DocsPanelState } from './misc/DocsPanel';
import { LeftFloatingColumn } from './misc/floating-boxes/LeftFloatingColumn';
import { RightFloatingColumn } from './misc/floating-boxes/RightFloatingColumn';
import { KeyboardListener } from './misc/KeyBoardListener';
import TransformMenu from './misc/TransformMenu';
import { ResizableSidebarDrawer } from './ResizableSidebarDrawer';
import { SharedComponentSetLookupCodeControl } from './SharedComponentSetLookupCodeControl';
import { StatusBar } from './toolbars/bottom/StatusBar';
import { LeftSidebarContents } from './toolbars/left/LeftSidebarContents';
import RightToolbar from './toolbars/right/RightToolbar';
import { MobileToolbar } from './toolbars/top/MobileToolbar';
import { StandardToolbar } from './toolbars/top/StandardToolbar';

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;

type Props = StateProps & DispatchProps & { inMobileMode: boolean };

interface State {
  readonly leftSidebarWidth: number;

  readonly rightSidebarWidth: number;
  readonly legacyRightSidebarWidth: number;

  readonly showUpgradeAccountDialog: boolean;

  readonly docsPanelState: DocsPanelState;
}

class RootComponent extends React.Component<Props, State> {
  mainContent: HTMLElement | null = null;

  constructor(props: Props) {
    super(props);

    this.state = {
      leftSidebarWidth: 300,
      rightSidebarWidth: 400,
      legacyRightSidebarWidth: 600,

      showUpgradeAccountDialog: Boolean(new URLSearchParams(window.location.search).get('upgradeAccount')),

      docsPanelState: 'closed',
    };

    const searchParameters = new URLSearchParams(window.location.search);
    if (searchParameters.has('welcome') || searchParameters.has('upgradeAccount')) {
      // most of the time our URL rewriting will clear the param, but if not take care of it here
      setTimeout(() => {
        const url = new URL(window.location.href);

        if (url.searchParams.has('welcome') || url.searchParams.has('upgradeAccount')) {
          url.searchParams.delete('welcome');
          url.searchParams.delete('upgradeAccount');
          window.history.replaceState({}, '', url.toString());
        }
      }, 5_000);
    }
  }

  renderRightSidebar() {
    const replacementPanel = this.renderRightSidebarReplacementPanel();

    return (
      <>
        {replacementPanel}
        <RightToolbar visible={replacementPanel === null} />
      </>
    );
  }

  renderRightSidebarReplacementPanel() {
    switch (this.props.rightSidebarInfo.mode) {
      case 'add-item':
        return <AddItemPanel />;

      case 'gable':
        return <MakeGablePanel />;

      case 'heel-macros':
        return <HeelMacrosPanel />;

      case 'importing-component':
        return <ImportComponentDesignPanel />;

      case 'match-fascia-heights':
        return <MatchFasciaHeightsPanel />;

      case 'optimization':
        return <OptimizationPanel />;

      case 'overhang-macros':
        return <OverhangMacrosPanel />;

      case 'replace-lumbers':
        return <ReplaceLumbersPanel />;

      case 'rename-layout-components':
        return <RenameLayoutComponentsPanel />;

      case 'reweb-profile':
        return <RewebProfileTrussPanel />;

      case 'reweb-zone':
        return <RewebZonePanel />;

      case 'settings':
        return <SettingsPanel />;

      case 'span-macros':
        return <SpanMacrosPanel />;

      case 'stub':
        return <StubTrussPanel />;

      case 'transform':
        return <TransformPanel />;

      case 'normal':
      case 'none':
        return null;

      default:
        assertExhaustiveSwitch(this.props.rightSidebarInfo.mode);
    }
  }

  renderPopUp() {
    if (this.props.showUploadDialog) {
      return <UploadDialog />;
    }

    if (this.props.snappingUnderlayStatus === 'loading' || this.props.snappingUnderlayStatus === 'pending') {
      return <UnderlayUploadDialog />;
    }

    switch (this.props.popup.popupName) {
      case BEARING_IMAGE_UPLOAD_DIALOG:
        return <BearingImageUploadDialog imageUrl={this.props.popup.options.imageUrl} />;

      case USER_SETTINGS_DIALOG:
        return <ConnectedUserSettingsDialog />;

      case COMPONENT_DELETION_CONFIRMATION_DIALOG:
        return <DeleteComponentDesignConfirmDialog componentDesignGuids={this.props.popup.options} />;

      case DELETE_ROOF_CONTAINER_CONFIRMATION_DIALOG:
        return (
          <DeleteRoofContainerConfirmDialog
            projectGuid={this.props.popup.options.projectGuid}
            roofContainer={this.props.popup.options.roofContainer}
          />
        );

      case CARRIED_BEARING_DELETION_CONFIRMATION_DIALOG:
        return (
          <CarriedBearingDeleteConfirmDialog
            componentDesign={this.props.popup.options.componentDesign}
            bearing={this.props.popup.options.bearing}
          />
        );

      case PARENT_TRUSS_GROUP_DELETION_CONFIRMATION_DIALOG:
        return (
          <ParentTrussGroupDeleteConfirmDialog
            parentTrussGroupGuidsBeingDeleted={this.props.popup.options.parentTrussGroupGuidsBeingDeleted}
            childTrussGroupsWhichWillBeCascadedDeleted={
              this.props.popup.options.childTrussGroupsWhichWillBeCascadedDeleted
            }
          />
        );

      case QUALITY_CONTROL_PDF_DOWNLOAD_DIALOG:
        return <QualityControlPdfDownloadDialog />;

      case QUALITY_CONTROL_DIGITAL_QC_DOWNLOAD_DIALOG:
        return <QualityControlDigitalQcDownloadDialog />;

      case SELECT_STATION_DIALOG:
        return <SelectStationDialog />;

      case SELECT_SCHEDULE_LINE_GROUP_DIALOG:
        return <SelectScheduleLineGroupDialog />;

      case IMPORT_ASSEMBLY_GROUP_DIALOG:
        return <ImportAssemblyGroupDialog />;

      case PARTS_DIALOG:
        return <PartsDialog />;

      case INSERTION_MENU_DIALOG:
        return <InsertionMenuDialog />;

      case FLIP_TRUSS_ENVELOPE_DIALOG:
        return <FlipTrussEnvelopeConfirmDialog flippedEnvelopes={this.props.popup.options.flippedEnvelopes} />;

      case SELECT_COMPONENT_DESIGN_DIALOG:
        return <SelectComponentDesignDialog {...this.props.popup.options} />;

      case CONFIRM_CANCEL_INSERTION_DIALOG:
        return <ConfirmCancelInsertionDialog />;

      case CONFIRM_REPLACE_LAYOUT_DIALOG:
        return <ReplaceLayoutConfirmDialog {...this.props.popup.options} />;

      case CONFIRM_AFFECT_ALL_LOCKS_DIALOG:
        return <ConfirmAffectAllLocksDialog {...this.props.popup.options} />;

      case CREATE_ESTIMATE_DIALOG:
        return <CreateEstimateDialog />;

      case HOTKEY_DIALOG:
        return <Hotkeys />;

      case LOG_IN_DIALOG:
        return <LogInDialog />;

      case SIGN_UP_DIALOG:
        return <SignUpDialog />;

      case SHARE_DIALOG:
        return <ShareDialog />;

      case CHOOSE_PROJECT_DIALOG:
        return (
          <ChooseProjectDialog
            initialTab={this.props.popup.options.initialTab}
            isStartup={this.props.popup.options.isStartup}
            reason={this.props.popup.options.reason}
          />
        );

      case WELCOME_DIALOG:
        return <WelcomeDialog />;

      case SHARED_COMPONENT_SET_INFO_DIALOG:
        return <SharedComponentSetInfoDialog />;

      case EDIT_DEPTH_DIALOG:
        return <EditDepthDialog />;

      case NOT_ON_MOBILE_DIALOG:
        return <NotOnMobileDialog featureText={this.props.popup.options.featureText} />;

      case PROJECT_WIDE_SETTINGS_DIALOG:
        return <ProjectWideSettingsDialog {...this.props.popup.options} />;

      case UPGRADE_ACCOUNT_DIALOG:
        return <UpgradeAccountDialog {...this.props.popup.options} />;

      case DOWNLOADS_DIALOG:
        return <DownloadsDialog />;

      case SELECT_PLATE_LISTS_DIALOG:
        return <DownloadPlateListComparisonDialog />;

      case PRESETS_DIALOG:
        return <PresetsDialog />;

      default:
        break;
    }

    // ReassociateOrphanedManualLoadsDialog takes precedence over this dialog, so this must happen after the `switch`.
    if (this.props.showAnalysisDialog) {
      return <AnalyzeDialog />;
    }

    if (this.state.showUpgradeAccountDialog) {
      return (
        <UpgradeAccountDialog
          onClose={() => {
            this.setState({ showUpgradeAccountDialog: false });
          }}
        />
      );
    }

    return null;
  }

  startApp = () => {
    this.props.startApp();
  };

  // The header button brings a minimized panel back up rather than closing it.
  toggleDocsPanel = () => {
    this.setState(({ docsPanelState }) => ({ docsPanelState: docsPanelState === 'expanded' ? 'closed' : 'expanded' }));
  };

  setDocsPanelState = (docsPanelState: DocsPanelState) => {
    this.setState({ docsPanelState });
  };

  render() {
    const messagesHtml: any = Array.from(this.props.spinnerMessages).map((text) => (
      <SpinnerMessage id={text} text={text} key={text} elevation={5} />
    ));

    const leftSidebarWidth = this.state.leftSidebarWidth;
    const rightSidebarWidth = this.props.useLegacyRightSidebarWidth
      ? this.state.legacyRightSidebarWidth
      : this.state.rightSidebarWidth;

    let leftSidebarOpen = (() => {
      switch (this.props.leftSidebarStatus) {
        case 'closed':
          return false;

        // per the `LeftSidebarType` definition: 'default' is closed on mobile or in a layout, open otherwise
        case 'default':
        case 'negateDefault':
          const defaultState = !(this.props.inMobileMode || this.props.layoutModeEnabled);
          return this.props.leftSidebarStatus === 'default' ? defaultState : !defaultState;

        case 'open':
        case 'analysisHistory':
        case 'notifications':
          return true;

        default:
          assertExhaustiveSwitch(this.props.leftSidebarStatus);
      }
    })();

    let rightSidebarOpen = this.props.rightSidebarInfo.mode !== 'none';

    if (this.props.inMobileMode && leftSidebarOpen && rightSidebarOpen) {
      leftSidebarOpen = this.props.preferredSidebar === 'left';
      rightSidebarOpen = this.props.preferredSidebar === 'right';
    }

    return (
      <>
        <CssBaseline />
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
            backgroundImage: `url(${backdropImageUrl})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundAttachment: 'fixed',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
            <LoginContainer
              onLogIn={this.startApp}
              logoOverride={(this.props.isUsingCustomTheme && <ThemedLogo width={300} />) || undefined}
              clientLogoUrl={designLogoUrl}
              clientName="Design"
              requirePermission="design-access"
              allowAnonymous={getIsInSharedComponentSetContext(this.props.sharedComponentSetState)}
              additionalLoginScreenContent={(panel) => panel === 'logIn' && <SharedComponentSetLookupCodeControl />}
            >
              <BrowserTabTitle />
              <TransformMenu />
              <Header onToggleDocsPanel={this.toggleDocsPanel} />

              <WalkthroughBlurbAnchor id="standard-toolbar" blurbPlacement="bottom">
                {this.props.inMobileMode ? <MobileToolbar /> : <StandardToolbar />}
              </WalkthroughBlurbAnchor>

              <NotifierDialog />

              {messagesHtml}

              <Box
                sx={{
                  flexGrow: 1,
                  height: 430,
                  zIndex: 1,
                  position: 'relative',
                  display: 'flex',
                  backgroundColor: 'background.default',
                }}
              >
                <WalkthroughBlurbAnchor id="left-sidebar" blurbPlacement="right">
                  <ResizableSidebarDrawer
                    open={leftSidebarOpen}
                    side="left"
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                    width={leftSidebarWidth}
                    setWidth={(width) => {
                      this.setState({ leftSidebarWidth: Math.max(width, 250) });
                    }}
                  >
                    <LeftSidebarContents />
                  </ResizableSidebarDrawer>
                </WalkthroughBlurbAnchor>

                <Box
                  component="main"
                  sx={[
                    {
                      flex: 1,
                      position: 'relative',
                      overflow: 'hidden',
                      marginRight: rightSidebarOpen ? 0 : `-${rightSidebarWidth}px`,
                    },
                  ]}
                  ref={(mainContent: HTMLElement) => {
                    this.mainContent = mainContent;
                  }}
                >
                  <LeftFloatingColumn leftSidebarOpen={leftSidebarOpen} rightSidebarOpen={rightSidebarOpen} />

                  <CoordinatesLabel />
                  <DesignClientNotifier />
                  <KeyboardListener />
                  <WalkthroughBlurbAnchor id="scene-canvas" blurbPlacement="centered">
                    <SceneCanvas />
                  </WalkthroughBlurbAnchor>

                  <RightFloatingColumn sidebarOpen={rightSidebarOpen} />
                </Box>

                <WalkthroughBlurbAnchor id="right-sidebar" blurbPlacement="left">
                  <ResizableSidebarDrawer
                    side="right"
                    occupySpaceWhenClosed
                    open={rightSidebarOpen}
                    sx={{ display: 'flex', flexDirection: 'column' }}
                    width={rightSidebarWidth}
                    setWidth={(width) => {
                      width = Math.max(width, 250);

                      if (this.props.useLegacyRightSidebarWidth) {
                        this.setState({ legacyRightSidebarWidth: width });
                      } else {
                        this.setState({ rightSidebarWidth: width });
                      }
                    }}
                  >
                    {this.renderRightSidebar()}
                  </ResizableSidebarDrawer>
                </WalkthroughBlurbAnchor>
              </Box>

              {this.renderPopUp()}

              <DocsPanel state={this.state.docsPanelState} onStateChange={this.setDocsPanelState} />
              <AssistantRoot />

              <StatusBar />
            </LoginContainer>
          </Box>
        </Box>
      </>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  popup: state.popup,
  layoutModeEnabled: isLayoutModeEnabled(state),
  leftSidebarStatus: state.panelData.leftSidebar,
  rightSidebarInfo: getRightSidebarInfo(state),
  useLegacyRightSidebarWidth: getShouldRightSidebarBeInLegacyMode(state),
  preferredSidebar: state.panelData.preferredSidebar,
  showAnalysisDialog: shouldShowAnalysisDialog(state),
  showUploadDialog: shouldShowUploadDialog(state),
  spinnerMessages: getSpinnersToDisplay(state),
  isUsingCustomTheme: isUsingCustomTheme(state),
  sharedComponentSetState: state.sharedComponentSet,
  snappingUnderlayStatus: state.snappingUnderlay.upload.status,
});

const actionCreators = {
  startApp,
};

export default connect(mapStateToProps, actionCreators)(withInMobileMode(RootComponent));
